class ScrollStageHero extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this._mask = this.querySelector('[data-scroll-stage-mask]');
    this._textLayers = Array.from(this.querySelectorAll('[data-scroll-stage-text]'));
    this._imageLayers = Array.from(this.querySelectorAll('[data-scroll-stage-image]'));
    this._stageCount = parseInt(this.dataset.stageCount || '1', 10);
    this._reducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this._mask || this._stageCount < 1) return;

    this._maxScale = this._computeMaxScale();
    this._onScroll = this._handleScroll.bind(this);
    this._onResize = this._handleResize.bind(this);

    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });

    if (this._reducedMotion) {
      this._applyReducedMotionState();
    } else {
      this._handleScroll();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    if (this._frame) {
      cancelAnimationFrame(this._frame);
      this._frame = 0;
    }
  }

  _clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  _computeMaxScale() {
    const baseSize = this._mask.offsetWidth || parseFloat(getComputedStyle(this._mask).width) || 1;
    const viewportMax = Math.max(window.innerWidth, window.innerHeight);
    return (viewportMax / baseSize) * 1.2;
  }

  _getProgress() {
    const section = this.closest('.shopify-section');
    if (!section) return 0;

    const scrollable = section.offsetHeight - window.innerHeight;
    if (scrollable <= 0) return 0;

    return this._clamp(-section.getBoundingClientRect().top / scrollable, 0, 1);
  }

  _getStageIndex(progress) {
    if (this._stageCount <= 1) return 0;
    return this._clamp(Math.floor(progress * this._stageCount), 0, this._stageCount - 1);
  }

  _setActiveStage(index) {
    this._textLayers.forEach((layer, layerIndex) => {
      const isActive = layerIndex === index;
      layer.classList.toggle('is-active', isActive);
      layer.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this._imageLayers.forEach((layer, layerIndex) => {
      layer.classList.toggle('is-active', layerIndex === index);
    });
  }

  _applyReducedMotionState() {
    const finalIndex = Math.max(0, this._stageCount - 1);
    this._mask.style.setProperty('--scroll-stage-scale', this._maxScale);
    this._mask.style.setProperty('--scroll-stage-scale-final', this._maxScale);
    this._setActiveStage(finalIndex);
  }

  _update(progress) {
    const scale = 1 + progress * (this._maxScale - 1);
    this._mask.style.setProperty('--scroll-stage-scale', scale);
    this._setActiveStage(this._getStageIndex(progress));
  }

  _handleResize() {
    this._maxScale = this._computeMaxScale();
    this._handleScroll();
  }

  _handleScroll() {
    if (this._reducedMotion) return;
    if (this._frame) return;

    this._frame = requestAnimationFrame(() => {
      this._frame = 0;
      this._update(this._getProgress());
    });
  }
}

if (!window.customElements.get('scroll-stage-hero')) {
  window.customElements.define('scroll-stage-hero', ScrollStageHero);
}
